import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { Tooltip, TooltipContent, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import { differenceInCalendarDays, differenceInMinutes, isSameYear } from 'date-fns';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type RelativeTimestampProps = {
    date: string | number | Date;
};

/**
 * Timestamp of a past event, e.g. when a comment was written. Recent events are
 * relative ("vor 12 Min.", "gestern, 14:32"), older ones show the exact date
 * ("12. Sept.", "12. Sept. 2025"). The full date and time is in the tooltip.
 */
export function RelativeTimestamp({ date }: RelativeTimestampProps): ReactNode {
    const { t } = useTranslation('shared');
    const locale = useResolvedLocale();

    const value = new Date(date);
    const now = new Date();

    const label = formatRelativeTimestamp(value, now, locale, t('relative_timestamp__just_now'));
    const fullLabel = new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'short' }).format(
        value,
    );

    return (
        <Tooltip>
            <TooltipTrigger render={<time dateTime={value.toISOString()} />}>{label}</TooltipTrigger>
            <TooltipContent side="top">{fullLabel}</TooltipContent>
        </Tooltip>
    );
}

function formatRelativeTimestamp(value: Date, now: Date, locale: string, justNow: string): string {
    const minutes = differenceInMinutes(now, value);
    const days = differenceInCalendarDays(now, value);
    const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
    const time = new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(value);

    if (minutes < 1) {
        return justNow;
    }
    if (minutes < 60) {
        return relative.format(-minutes, 'minute');
    }
    if (days === 0) {
        return relative.format(-Math.floor(minutes / 60), 'hour');
    }
    if (days === 1) {
        return `${relative.format(-1, 'day')}, ${time}`;
    }
    if (days < 7) {
        return new Intl.DateTimeFormat(locale, {
            weekday: 'short',
            hour: 'numeric',
            minute: '2-digit',
        }).format(value);
    }
    if (isSameYear(value, now)) {
        return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(value);
    }
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(value);
}
